import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RaceEvent } from '../types';
import { raceService } from '../services/raceService';
import EventHelpModal from '../components/EventHelpModal';

/**
 * Race Events Dashboard — displays all available race events with filtering.
 *
 * Requirements:
 *  10.1 — Display all available Race_Events
 *  10.2 — Show Race_Event name, date, and category level
 *  10.3 — Navigate to Compliance Dashboard on event selection
 *  10.5 — Filter Race_Events by date range or category
 */
const RaceEventsPage: React.FC = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState<RaceEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [askEvent, setAskEvent] = useState<{ raceId: string; name: string } | null>(null);

  /** Fetch race events from the API. */
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await raceService.getRaces();
      setEvents(data);
    } catch {
      setError('Failed to load race events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  /** Derive unique sorted categories from loaded events for the filter dropdown. */
  const categories = useMemo(() => {
    const unique = Array.from(new Set(events.map((e) => e.category)));
    unique.sort();
    return unique;
  }, [events]);

  /** Client-side filtered events based on year range and category. */
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (yearFrom && event.year < Number(yearFrom)) return false;
      if (yearTo && event.year > Number(yearTo)) return false;
      if (categoryFilter && event.category !== categoryFilter) return false;
      return true;
    });
  }, [events, yearFrom, yearTo, categoryFilter]);

  /** Navigate to the compliance dashboard for the selected race. */
  const handleEventClick = (raceId: string) => {
    navigate(`/events/${raceId}/compliance`);
  };

  const handleClearFilters = () => {
    setYearFrom('');
    setYearTo('');
    setCategoryFilter('');
  };

  return (
    <div className="race-events-page">
      <h1>Race Events</h1>

      {/* Filters */}
      <div className="race-events-filters" data-testid="filters">
        <label htmlFor="yearFrom">
          Year From:
          <input
            id="yearFrom"
            type="number"
            value={yearFrom}
            onChange={(e) => setYearFrom(e.target.value)}
            placeholder="e.g. 2024"
            min="2000"
            max="2100"
          />
        </label>

        <label htmlFor="yearTo">
          Year To:
          <input
            id="yearTo"
            type="number"
            value={yearTo}
            onChange={(e) => setYearTo(e.target.value)}
            placeholder="e.g. 2026"
            min="2000"
            max="2100"
          />
        </label>

        <label htmlFor="categoryFilter">
          Category:
          <select
            id="categoryFilter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </label>

        <button type="button" onClick={handleClearFilters} className="clear-filters-btn">
          Clear Filters
        </button>
      </div>

      {/* Content */}
      {loading && <p data-testid="loading-indicator">Loading race events…</p>}

      {error && (
        <p className="error-message" data-testid="error-message">
          {error}
        </p>
      )}

      {!loading && !error && filteredEvents.length === 0 && (
        <p data-testid="no-events">No race events found.</p>
      )}

      {!loading && !error && filteredEvents.length > 0 && (
        <table className="race-events-table" data-testid="events-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Year</th>
              <th>Category</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map((event) => (
              <tr
                key={event.raceId}
                onClick={() => handleEventClick(event.raceId)}
                className="race-event-row"
                data-testid={`event-row-${event.raceId}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleEventClick(event.raceId);
                  }
                }}
              >
                <td>{event.name}</td>
                <td>{event.year}</td>
                <td>{event.category}</td>
                <td>
                  <button
                    className="ask-event-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAskEvent({ raceId: event.raceId, name: event.name });
                    }}
                    aria-label={`Ask about ${event.name}`}
                  >
                    Ask
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {askEvent && (
        <EventHelpModal
          isOpen={true}
          onClose={() => setAskEvent(null)}
          raceId={askEvent.raceId}
          eventName={askEvent.name}
        />
      )}
    </div>
  );
};

export default RaceEventsPage;
